import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { comprehensiveHRService } from '../../../lib/checkInAttendanceService';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ComplianceDocuments = ({ hrProfiles, loading, gymId, managerId }) => {
  const [documents, setDocuments] = useState([]);
  const [certifications, setCertifications] = useState([]);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadType, setUploadType] = useState('document');
  const [expiringItems, setExpiringItems] = useState([]);
  const [docsLoading, setDocsLoading] = useState(false);

  useEffect(() => {
    if (gymId) {
      loadExpiringItems();
    }
  }, [gymId]);

  useEffect(() => {
    if (selectedStaff) {
      loadStaffCompliance();
    }
  }, [selectedStaff]);

  const loadExpiringItems = async () => {
    const { data } = await comprehensiveHRService?.getExpiringCertifications(gymId, 60);
    if (data) setExpiringItems(data);
  };

  const loadStaffCompliance = async () => {
    if (!selectedStaff) return;
    setDocsLoading(true);

    const [docsRes, certsRes] = await Promise.all([
      comprehensiveHRService?.getStaffDocuments(selectedStaff),
      comprehensiveHRService?.getStaffCertifications(selectedStaff)
    ]);

    if (docsRes?.data) setDocuments(docsRes?.data);
    if (certsRes?.data) setCertifications(certsRes?.data);

    setDocsLoading(false);
  };

  const handleUploadDocument = async (docData) => {
    const hrProfile = hrProfiles?.find((p) => p?.staff_id === selectedStaff);
    if (!hrProfile) return;

    const { error } = await comprehensiveHRService?.createComplianceDocument({
      gymId,
      staffId: selectedStaff,
      hrProfileId: hrProfile?.id,
      documentType: docData?.documentType,
      documentName: docData?.documentName,
      documentUrl: docData?.documentUrl,
      issueDate: docData?.issueDate,
      expiryDate: docData?.expiryDate,
      notes: docData?.notes
    });

    if (!error) {
      setShowUploadModal(false);
      loadStaffCompliance();
      loadExpiringItems();
    }
  };

  const handleAddCertification = async (certData) => {
    const hrProfile = hrProfiles?.find((p) => p?.staff_id === selectedStaff);
    if (!hrProfile) return;

    const { error } = await comprehensiveHRService?.createCertification({
      gymId,
      staffId: selectedStaff,
      hrProfileId: hrProfile?.id,
      certificationName: certData?.certificationName,
      certificationType: certData?.certificationType,
      issuingOrganization: certData?.issuingOrganization,
      certificationNumber: certData?.certificationNumber,
      issueDate: certData?.issueDate,
      expiryDate: certData?.expiryDate,
      notes: certData?.notes
    });

    if (!error) {
      setShowUploadModal(false);
      loadStaffCompliance();
      loadExpiringItems();
    }
  };

  const handleVerifyDocument = async (docId) => {
    const { error } = await comprehensiveHRService?.verifyDocument(docId, managerId);
    if (!error) {
      loadStaffCompliance();
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-success/10 text-success';
      case 'expired':
        return 'bg-error/10 text-error';
      case 'pending_renewal':
        return 'bg-warning/10 text-warning';
      case 'revoked':
        return 'bg-error/10 text-error';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const getDocumentTypeIcon = (type) => {
    switch (type) {
      case 'contract':
        return 'FileText';
      case 'certification':
        return 'Award';
      case 'nda':
        return 'Lock';
      case 'background_check':
        return 'Shield';
      case 'id_document':
        return 'CreditCard';
      case 'tax_form':
        return 'Receipt';
      default:
        return 'File';
    }
  };

  const totalDocs = documents?.length || 0;
  const verifiedDocs = documents?.filter((d) => d?.is_verified)?.length || 0;
  const activeCerts = certifications?.filter((c) => c?.status === 'active')?.length || 0;
  const expiringCount = expiringItems?.length || 0;

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="card">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-lg bg-primary/10">
              <Icon name="FileText" size={24} className="text-primary" />
            </div>
            <div>
              <div className="text-2xl font-bold text-foreground">{totalDocs}</div>
              <div className="text-xs text-muted-foreground">Total Documents</div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-lg bg-success/10">
              <Icon name="CheckCircle" size={24} className="text-success" />
            </div>
            <div>
              <div className="text-2xl font-bold text-foreground">{verifiedDocs}</div>
              <div className="text-xs text-muted-foreground">Verified</div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-lg bg-primary/10">
              <Icon name="Award" size={24} className="text-primary" />
            </div>
            <div>
              <div className="text-2xl font-bold text-foreground">{activeCerts}</div>
              <div className="text-xs text-muted-foreground">Active Certifications</div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-lg bg-warning/10">
              <Icon name="AlertCircle" size={24} className="text-warning" />
            </div>
            <div>
              <div className="text-2xl font-bold text-foreground">{expiringCount}</div>
              <div className="text-xs text-muted-foreground">Expiring Soon</div>
            </div>
          </div>
        </div>
      </div>

      {/* Expiring Items Alert */}
      {expiringItems?.length > 0 && (
        <div className="card bg-warning/5 border border-warning/20">
          <div className="flex items-start gap-3">
            <Icon name="AlertTriangle" size={24} className="text-warning mt-1" />
            <div className="flex-1">
              <h3 className="font-semibold text-foreground mb-2">Certifications Expiring Soon</h3>
              <div className="space-y-2">
                {expiringItems?.slice(0, 5)?.map((item) => (
                  <div key={item?.certification_id} className="flex items-center justify-between text-sm">
                    <span className="text-foreground">
                      {item?.staff_name} - {item?.certification_name}
                    </span>
                    <span className="text-warning font-medium">
                      {item?.days_until_expiry} days remaining
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Staff Selection */}
      <div className="card">
        <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
          <div className="flex-1">
            <label className="block text-sm font-medium text-foreground mb-2">Select Staff Member</label>
            <select
              value={selectedStaff || ''}
              onChange={(e) => setSelectedStaff(e?.target?.value || null)}
              className="w-full px-4 py-2 rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="">Choose a staff member...</option>
              {hrProfiles?.map((profile) => (
                <option key={profile?.staff_id} value={profile?.staff_id}>
                  {profile?.staff?.full_name} - {profile?.position}
                </option>
              ))}
            </select>
          </div>

          {selectedStaff && (
            <Button onClick={() => setShowUploadModal(true)}>
              <Icon name="Upload" size={18} />
              <span>Add Document/Cert</span>
            </Button>
          )}
        </div>
      </div>

      {!selectedStaff ? (
        <div className="card text-center py-12">
          <Icon name="FileText" size={48} className="text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-foreground mb-2">Select a Staff Member</h3>
          <p className="text-muted-foreground">Choose a staff member to view their compliance documents</p>
        </div>
      ) : docsLoading ? (
        <div className="flex items-center justify-center py-12">
          <Icon name="Loader2" size={32} className="animate-spin text-primary" />
        </div>
      ) : (
        <>
          {/* Certifications */}
          <div className="card">
            <h3 className="text-xl font-semibold text-foreground mb-4 flex items-center gap-2">
              <Icon name="Award" size={20} className="text-primary" />
              Certifications
            </h3>
            {certifications?.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">No certifications found</p>
            ) : (
              <div className="space-y-3">
                {certifications?.map((cert) => (
                  <div key={cert?.id} className="p-4 rounded-lg bg-muted/30">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <div className="font-semibold text-foreground">{cert?.certification_name}</div>
                        <div className="text-sm text-muted-foreground">{cert?.issuing_organization}</div>
                      </div>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(cert?.status)}`}>
                        {cert?.status?.replace('_', ' ')}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      {cert?.certification_type && (
                        <div>
                          <div className="text-muted-foreground">Type</div>
                          <div className="font-medium text-foreground">{cert?.certification_type}</div>
                        </div>
                      )}
                      {cert?.issue_date && (
                        <div>
                          <div className="text-muted-foreground">Issued</div>
                          <div className="font-medium text-foreground">
                            {format(new Date(cert?.issue_date), 'MMM d, yyyy')}
                          </div>
                        </div>
                      )}
                      {cert?.expiry_date && (
                        <div>
                          <div className="text-muted-foreground">Expires</div>
                          <div className="font-medium text-foreground">
                            {format(new Date(cert?.expiry_date), 'MMM d, yyyy')}
                          </div>
                        </div>
                      )}
                      {cert?.certification_number && (
                        <div>
                          <div className="text-muted-foreground">Number</div>
                          <div className="font-medium text-foreground">{cert?.certification_number}</div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Documents */}
          <div className="card">
            <h3 className="text-xl font-semibold text-foreground mb-4 flex items-center gap-2">
              <Icon name="FileText" size={20} className="text-primary" />
              Compliance Documents
            </h3>
            {documents?.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">No documents found</p>
            ) : (
              <div className="space-y-3">
                {documents?.map((doc) => (
                  <div key={doc?.id} className="p-4 rounded-lg bg-muted/30">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-start gap-3">
                        <div className="p-2 rounded-lg bg-primary/10">
                          <Icon name={getDocumentTypeIcon(doc?.document_type)} size={20} className="text-primary" />
                        </div>
                        <div>
                          <div className="font-semibold text-foreground">{doc?.document_name}</div>
                          <div className="text-sm text-muted-foreground capitalize">
                            {doc?.document_type?.replace('_', ' ')}
                          </div>
                        </div>
                      </div>
                      {doc?.is_verified ? (
                        <span className="px-2 py-1 text-xs font-medium rounded-full bg-success/10 text-success flex items-center gap-1">
                          <Icon name="CheckCircle" size={14} />
                          Verified
                        </span>
                      ) : (
                        <Button size="sm" onClick={() => handleVerifyDocument(doc?.id)}>
                          <Icon name="Check" size={14} />
                          Verify
                        </Button>
                      )}
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                      {doc?.issue_date && (
                        <div>
                          <div className="text-muted-foreground">Issued</div>
                          <div className="font-medium text-foreground">
                            {format(new Date(doc?.issue_date), 'MMM d, yyyy')}
                          </div>
                        </div>
                      )}
                      {doc?.expiry_date && (
                        <div>
                          <div className="text-muted-foreground">Expires</div>
                          <div className="font-medium text-foreground">
                            {format(new Date(doc?.expiry_date), 'MMM d, yyyy')}
                          </div>
                        </div>
                      )}
                      {doc?.verified_at && (
                        <div>
                          <div className="text-muted-foreground">Verified By</div>
                          <div className="font-medium text-foreground">{doc?.verifier?.full_name}</div>
                        </div>
                      )}
                    </div>
                    {doc?.notes && (
                      <div className="mt-2 pt-2 border-t border-border text-sm text-muted-foreground">
                        {doc?.notes}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="card max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-foreground">Add Compliance Item</h3>
              <button
                onClick={() => setShowUploadModal(false)}
                className="text-muted-foreground hover:text-foreground"
              >
                <Icon name="X" size={24} />
              </button>
            </div>

            <div className="mb-4">
              <div className="flex gap-2">
                <button
                  onClick={() => setUploadType('document')}
                  className={`flex-1 py-2 px-4 rounded-lg font-medium transition-colors ${
                    uploadType === 'document'
                      ? 'bg-primary text-white' :'bg-muted text-muted-foreground hover:bg-muted/70'
                  }`}
                >
                  Document
                </button>
                <button
                  onClick={() => setUploadType('certification')}
                  className={`flex-1 py-2 px-4 rounded-lg font-medium transition-colors ${
                    uploadType === 'certification'
                      ? 'bg-primary text-white' :'bg-muted text-muted-foreground hover:bg-muted/70'
                  }`}
                >
                  Certification
                </button>
              </div>
            </div>

            {uploadType === 'document' ? (
              <form
                onSubmit={(e) => {
                  e?.preventDefault();
                  const formData = new FormData(e?.target);
                  handleUploadDocument({
                    documentType: formData?.get('documentType'),
                    documentName: formData?.get('documentName'),
                    documentUrl: formData?.get('documentUrl'),
                    issueDate: formData?.get('issueDate'),
                    expiryDate: formData?.get('expiryDate'),
                    notes: formData?.get('notes')
                  });
                }}
                className="space-y-4"
              >
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Document Type *</label>
                  <select
                    name="documentType"
                    required
                    className="w-full px-4 py-2 rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="contract">Contract</option>
                    <option value="certification">Certification</option>
                    <option value="nda">NDA</option>
                    <option value="background_check">Background Check</option>
                    <option value="id_document">ID Document</option>
                    <option value="tax_form">Tax Form</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Document Name *</label>
                  <input
                    type="text"
                    name="documentName"
                    required
                    className="w-full px-4 py-2 rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Document URL</label>
                  <input
                    type="url"
                    name="documentUrl"
                    placeholder="https://..."
                    className="w-full px-4 py-2 rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Issue Date</label>
                    <input
                      type="date"
                      name="issueDate"
                      className="w-full px-4 py-2 rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Expiry Date</label>
                    <input
                      type="date"
                      name="expiryDate"
                      className="w-full px-4 py-2 rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Notes</label>
                  <textarea
                    name="notes"
                    rows={3}
                    className="w-full px-4 py-2 rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>

                <div className="flex gap-2">
                  <Button type="submit" className="flex-1">
                    Add Document
                  </Button>
                  <Button type="button" variant="outline" onClick={() => setShowUploadModal(false)}>
                    Cancel
                  </Button>
                </div>
              </form>
            ) : (
              <form
                onSubmit={(e) => {
                  e?.preventDefault();
                  const formData = new FormData(e?.target);
                  handleAddCertification({
                    certificationName: formData?.get('certificationName'),
                    certificationType: formData?.get('certificationType'),
                    issuingOrganization: formData?.get('issuingOrganization'),
                    certificationNumber: formData?.get('certificationNumber'),
                    issueDate: formData?.get('issueDate'),
                    expiryDate: formData?.get('expiryDate'),
                    notes: formData?.get('notes')
                  });
                }}
                className="space-y-4"
              >
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Certification Name *</label>
                  <input
                    type="text"
                    name="certificationName"
                    required
                    placeholder="e.g., CPR Certification"
                    className="w-full px-4 py-2 rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Type</label>
                  <input
                    type="text"
                    name="certificationType"
                    placeholder="e.g., Safety, Professional"
                    className="w-full px-4 py-2 rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Issuing Organization</label>
                  <input
                    type="text"
                    name="issuingOrganization"
                    placeholder="e.g., American Red Cross"
                    className="w-full px-4 py-2 rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Certification Number</label>
                  <input
                    type="text"
                    name="certificationNumber"
                    className="w-full px-4 py-2 rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Issue Date</label>
                    <input
                      type="date"
                      name="issueDate"
                      className="w-full px-4 py-2 rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Expiry Date</label>
                    <input
                      type="date"
                      name="expiryDate"
                      className="w-full px-4 py-2 rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Notes</label>
                  <textarea
                    name="notes"
                    rows={2}
                    className="w-full px-4 py-2 rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>

                <div className="flex gap-2">
                  <Button type="submit" className="flex-1">
                    Add Certification
                  </Button>
                  <Button type="button" variant="outline" onClick={() => setShowUploadModal(false)}>
                    Cancel
                  </Button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ComplianceDocuments;